import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import { gsap } from "gsap";
import { useNavigate } from "react-router-dom";
import { usePostHog } from 'posthog-js/react'

import "./AddTaskModal.css";
import "./ResponsiveModal.css";
import Select from "react-select";
import { NotificationsContext } from "../context/NotificationsContext";
import getApiUrl from "../utils/api";
import { toast } from "react-toastify";

const AddTaskModal = ({ open, onClose, onTaskCreated }) => {
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState([]); // Multi-select array
  const [selectedGroup, setSelectedGroup] = useState("");
  const [assignType, setAssignType] = useState("members"); // "members" or "group" or "multi-members"
  const [selectionMode, setSelectionMode] = useState("single"); // "single" or "multi"
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");

  // New state for form fields
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("");
  const [project, setProject] = useState("");
  const [startDate, setStartDate] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const { addNotification } = useContext(NotificationsContext);
  const posthog = usePostHog();

  const handleAttachmentInput = (event) => {
    const incoming = Array.from(event.target.files || []);
    if (incoming.length === 0) {
      return;
    }

    let message = "";
    const next = [...attachments];

    for (const file of incoming) {
      if (next.length >= MAX_TASK_ATTACHMENTS) {
        message = `You can attach up to ${MAX_TASK_ATTACHMENTS} files per task.`;
        break;
      }
      if (file.size > MAX_TASK_ATTACHMENT_BYTES) {
        message = `"${file.name}" exceeds the 15 MB limit and was skipped.`;
        continue;
      }
      const duplicate = next.some(
        (existing) =>
          existing.name === file.name &&
          existing.lastModified === file.lastModified &&
          existing.size === file.size
      );
      if (duplicate) {
        continue;
      }
      next.push(file);
    }

    setAttachments(next);
    setAttachmentError(message);

    if (event.target) {
      event.target.value = "";
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== index));
    setAttachmentError("");
  };

  // Handle project selection
  const handleProjectChange = (selectedOption) => {
    if (selectedOption && selectedOption.value === "create_project") {
      // Navigate to Project page to create new project
      onClose(); // Close the modal first
      navigate("/project");
    } else {
      setProject(selectedOption ? selectedOption.value : "");
    }
  };

  // Prepare project options for dropdown
  const projectOptions = [
    { value: "", label: "Select Project" },
    ...(Array.isArray(projects)
      ? projects.map(p => ({ value: p.id, label: p.name }))
      : []),
    {
      value: "create_project",
      label: "+ Create New Project",
      className: "create-project-option",
    },
  ];

  const priorityOptions = [
    { value: "important", label: "Important" },
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
  ];

  const MAX_TASK_ATTACHMENTS = 5;
  const MAX_TASK_ATTACHMENT_BYTES = 15 * 1024 * 1024;

  const formatFileSize = (bytes) => {
    if (!bytes || Number.isNaN(Number(bytes)) || Number(bytes) <= 0) {
      return "";
    }
    const units = ["B", "KB", "MB", "GB"];
    let value = Number(bytes);
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    const rounded = value >= 100 || value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
    return `${rounded} ${units[unitIndex]}`;
  };

  useEffect(() => {
    if (open) {
      // Show modal with animation
      gsap.set(overlayRef.current, { display: "flex" });
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: "power2.out" }
      );

      gsap.fromTo(
        contentRef.current,
        {
          opacity: 0,
          y: -50,
          scale: 0.9,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.4,
          ease: "back.out(1.7)",
          delay: 0.1,
        }
      );

      setAttachments([]);
      setAttachmentError("");

      // Always resolve current user from server to avoid stale localStorage
      fetch(getApiUrl("api/auth/me"), { credentials: "include" })
        .then((r) => r.json())
        .then((d) =>
          setCurrentUserId(
            d?.user?.userId || d?.user?.id || d?.user?.sub || null
          )
        )
        .catch(() => {
          // Best-effort fallback to localStorage if server fails
          try {
            const stored = localStorage.getItem("user");
            if (stored) {
              const u = JSON.parse(stored);
              setCurrentUserId(u.userId || u.id || u.sub || null);
            }
          } catch { }
        });

      // Fetch employees
      setLoadingEmployees(true);
      fetch(getApiUrl("api/auth/employees"), { credentials: "include" })
        .then((res) => res.json())
        .then((data) => {
          setEmployees(data);
          setLoadingEmployees(false);
        })
        .catch((err) => {
          console.error("Failed to fetch employees", err);
          setLoadingEmployees(false);
        });

      // Fetch projects
      setLoadingProjects(true);
      fetch(getApiUrl("api/project"), { credentials: "include" })
        .then((res) => res.json())
        .then((data) => {
          setProjects(data);
          setLoadingProjects(false);
        })
        .catch((err) => {
          console.error("Failed to fetch projects", err);
          setLoadingProjects(false);
        });
    } else {
      // Hide modal with animation
      gsap.to(contentRef.current, {
        opacity: 0,
        y: -30,
        scale: 0.95,
        duration: 0.2,
        ease: "power2.in",
      });

      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.3,
        ease: "power2.in",
        delay: 0.1,
        onComplete: () => {
          gsap.set(overlayRef.current, { display: "none" });
        },
      });
      setSelectedEmployee(""); // Reset selection when modal closes
      setSelectedEmployees([]); // Reset multi-select
      setSelectedGroup(""); // Reset group selection
      setAssignType("members"); // Reset to default
      setSelectionMode("single"); // Reset to single
      setEmployeeSearchTerm("");
      setTitle("");
      setPriority("");
      setProject("");
      setStartDate("");
      setTargetDate("");
      setDescription("");
      setAttachments([]);
      setAttachmentError("");
      setError("");
    }
  }, [open]);

  const filteredEmployees = useMemo(() => {
    const term = employeeSearchTerm.trim().toLowerCase();
    if (!term) {
      return employees;
    }

    return employees.filter((emp) => {
      const name = `${emp?.FirstName || ""} ${emp?.LastName || ""}`.toLowerCase();
      const email = (emp?.email || "").toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [employees, employeeSearchTerm]);

  const visibleEmployeeIds = useMemo(
    () => filteredEmployees.map((emp) => emp.id),
    [filteredEmployees]
  );

  const allVisibleSelected = useMemo(() => {
    if (visibleEmployeeIds.length === 0) {
      return false;
    }
    return visibleEmployeeIds.every((id) => selectedEmployees.includes(id));
  }, [visibleEmployeeIds, selectedEmployees]);

  // Fetch groups when assignType changes to "group"
  useEffect(() => {
    if (assignType === "group" && currentUserId) {
      setLoadingGroups(true);
      fetch(getApiUrl("api/group"), { credentials: "include" })
        .then((res) => res.json())
        .then((data) => {
          // Filter groups created by current user
          const userGroups = Array.isArray(data)
            ? data.filter((group) => {
              return group.created_by === parseInt(currentUserId);
            })
            : [];

          // If user hasn't created any groups, show all groups as fallback
          if (
            userGroups.length === 0 &&
            Array.isArray(data) &&
            data.length > 0
          ) {
            setGroups(data);
          } else {
            setGroups(userGroups);
          }

          setLoadingGroups(false);
        })
        .catch((err) => {
          console.error("Failed to fetch groups", err);
          setLoadingGroups(false);
        });
    }
  }, [assignType, currentUserId]);

  const handleClose = () => {
    // Animate close before calling onClose
    gsap.to(contentRef.current, {
      opacity: 0,
      y: -30,
      scale: 0.95,
      duration: 0.2,
      ease: "power2.in",
    });

    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.3,
      ease: "power2.in",
      delay: 0.1,
      onComplete: onClose,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setAttachmentError("");

    if (!priority) {
      toast.error("Please select a priority level.");
      setLoading(false);
      return;
    }

    if (!targetDate) {
      toast.error("Target End Date is required.");
      setLoading(false);
      return;
    }

    // Validate selection based on assignType and selectionMode
    if (assignType === "members") {
      if (selectionMode === "single" && !selectedEmployee) {
        toast.error("Please select an employee.");
        setLoading(false);
        return;
      }
      if (selectionMode === "multi" && selectedEmployees.length === 0) {
        toast.error("Please select at least one employee.");
        setLoading(false);
        return;
      }
    }

    if (assignType === "group" && !selectedGroup) {
      toast.error("Please select a group.");
      setLoading(false);
      return;
    }

    if (attachments.length > MAX_TASK_ATTACHMENTS) {
      setAttachmentError(
        `You can attach up to ${MAX_TASK_ATTACHMENTS} files per task.`
      );
      setLoading(false);
      return;
    }

    for (const fileItem of attachments) {
      if (fileItem.size > MAX_TASK_ATTACHMENT_BYTES) {
        setAttachmentError(`"${fileItem.name}" exceeds the 15 MB limit.`);
        setLoading(false);
        return;
      }
    }

    // Submit button animation
    const submitBtn = e.target.querySelector(".modal-submit");
    gsap.to(submitBtn, {
      scale: 0.95,
      duration: 0.1,
      yoyo: true,
      repeat: 1,
      ease: "power2.out",
    });

    // Prepare FormData
    const formData = new FormData();
    formData.append("title", title);
    formData.append("priority", priority);
    formData.append("project_id", project);
    if (startDate) {
      formData.append("start_date", startDate);
    }
    formData.append("due_date", targetDate);
    formData.append("description", description);

    // Set assigned_to based on assignType and selectionMode
    if (assignType === "members") {
      if (selectionMode === "single") {
        // Single user assignment
        formData.append("assigned_to", selectedEmployee);
      } else if (selectionMode === "multi") {
        // Multi-user assignment - create group automatically
        if (selectedEmployees.length === 1) {
          // If only one user selected, treat as single assignment
          formData.append("assigned_to", selectedEmployees[0]);
        } else {
          // Multiple users - backend will auto-create group with name: {taskname}_group
          formData.append("assignees", JSON.stringify(selectedEmployees));
          // Also set first as assigned_to for compatibility
          formData.append("assigned_to", selectedEmployees[0]);
        }
      }
    } else if (assignType === "group") {
      // For group assignment
      const selectedGroupData = groups.find((g) => g.id === selectedGroup);
      if (
        selectedGroupData &&
        selectedGroupData.members &&
        selectedGroupData.members.length > 0
      ) {
        formData.append("assigned_to", selectedGroupData.members[0].id);
        // Store group info
        formData.append("group_id", selectedGroup);
      } else {
        toast.error("Selected group has no members.");
        setLoading(false);
        return;
      }
    }

    // Set creator from logged-in user (guarantee from server if missing)
    let creatorId = currentUserId;
    if (!creatorId) {
      try {
        const meRes = await fetch(getApiUrl("api/auth/me"), {
          credentials: "include",
        });
        const me = await meRes.json();
        creatorId = me?.user?.userId || me?.user?.id || me?.user?.sub || null;
      } catch { }
    }
    if (creatorId) {
      formData.append("created_by", String(creatorId));
    }
    formData.append("department_id", "1"); // Replace as needed
    formData.append("status", "Pending"); // Default status
    formData.append("kpi", ""); // Set as needed
    attachments.forEach((fileItem) => {
      formData.append("taskAttachments", fileItem);
    });

    try {
      const response = await fetch(getApiUrl("api/task/createTask"), {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (response.status === 201) {
        try {
          const data = await response.json();
          let assignerName;
          try {
            const meRes = await fetch(getApiUrl("api/auth/me"), {
              credentials: "include",
            });
            const me = await meRes.json();
            assignerName = me?.user
              ? `${me.user.FirstName || ""} ${me.user.LastName || ""}`.trim()
              : undefined;
          } catch { }

          // Notify assignees
          if (assignType === "members") {
            let creatorIdSafe = creatorId;
            if (!creatorIdSafe) {
              try {
                const meRes2 = await fetch(getApiUrl("api/auth/me"), {
                  credentials: "include",
                });
                const me2 = await meRes2.json();
                creatorIdSafe =
                  me2?.user?.userId || me2?.user?.id || me2?.user?.sub || null;
              } catch { }
            }

            if (selectionMode === "multi" && selectedEmployees.length > 0) {
              // Notify all multi-selected users
              selectedEmployees.forEach((employeeId) => {
                if (
                  !creatorIdSafe ||
                  String(employeeId) !== String(creatorIdSafe)
                ) {
                  addNotification({
                    type: "task_assigned",
                    title: "New task assigned",
                    message: title
                      ? `"${title}" has been assigned to your group.`
                      : "A new task has been assigned to your group.",
                    meta: {
                      taskId: data?.task?.id || data?.id,
                      assignedToId: employeeId,
                      taskTitle: title,
                      assignerName,
                      isGroupAssignment: true,
                    },
                    targetUserId: employeeId,
                  });
                }
              });
            } else if (selectionMode === "single" && selectedEmployee) {
              // Single user notification
              if (
                !creatorIdSafe ||
                String(selectedEmployee) !== String(creatorIdSafe)
              ) {
                addNotification({
                  type: "task_assigned",
                  title: "New task assigned",
                  message: title
                    ? `"${title}" has been assigned to you.`
                    : "A new task has been assigned to you.",
                  meta: {
                    taskId: data?.task?.id || data?.id,
                    assignedToId: selectedEmployee,
                    taskTitle: title,
                    assignerName,
                  },
                  targetUserId: selectedEmployee,
                });
              }
            }
          }
        } catch { }
        setAttachments([]);
        setAttachmentError("");
        posthog.capture('Task Created', {
          taskName: title,
        });
        if (onTaskCreated) onTaskCreated();
        handleClose();
        return;
      }

      if (!response.ok) {
        let errorMessage = "Unknown error";
        let errorDetails = {};

        const errorData = await response?.json();
        errorDetails = errorData;
        errorMessage = errorData?.message || errorData?.error || JSON.stringify(errorData);

        posthog.capture('Task Creation Failed', {
          taskName: title,
          status: response.status,
          statusText: response.statusText,
          serverError: errorDetails,
        });
        throw new Error("Failed to create task");
      }

      try {
        const data = await response.json();

        addNotification({
          type: "task_created",
          title: "Task created",
          message: title
            ? `"${title}" has been created.`
            : "A new task has been created.",
          meta: { taskId: data?.task?.id || data?.id },
        });

      } catch { }
      if (onTaskCreated) onTaskCreated();
      handleClose();
    } catch (err) {
      toast.error(`Error: ${err.message || "Something went wrong"}`);
    } finally {
      setLoading(false);
    }
  };

  const employeeOptions = employees.map((emp) => ({
    value: emp.id,
    label: `${emp.FirstName} ${emp.LastName} (${emp.email})`,
  }));

  const groupOptions = groups.map((group) => ({
    value: group.id,
    label: `${group.groupName} (${group.members ? group.members.length : 0
      } members)`,
  }));

  // If no groups created by user, show all groups as fallback
  const allGroupOptions = groups.length === 0 ? [] : groupOptions;
  const hasUserGroups = groups.length > 0;
  const isShowingAllGroups =
    groups.length > 0 &&
    !groups.some((g) => g.created_by === parseInt(currentUserId));

  const handleAssignTypeChange = (type) => {
    setAssignType(type);
    // Clear previous selections when switching
    setSelectedEmployee("");
    setSelectedEmployees([]);
    setSelectedGroup("");
    setSelectionMode("single"); // Reset to single mode
  };

  const handleEmployeeCheckboxChange = (employeeId) => {
    setSelectedEmployees((prev) => {
      if (prev.includes(employeeId)) {
        return prev.filter((id) => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  const toggleSelectionMode = () => {
    setSelectionMode(selectionMode === "single" ? "multi" : "single");
    // Clear selections when switching modes
    setSelectedEmployee("");
    setSelectedEmployees([]);
  };

  return (
    <div
      ref={overlayRef}
      className="modal-overlay modal-overlay-responsive"
      style={{ display: "none" }}
    >
      <div ref={contentRef} className="modal-content modal-content-responsive">
        <button
          className="modal-close modal-close-responsive"
          onClick={handleClose}
        >
          &times;
        </button>
        <div className="modal-header-responsive gradient-header">
          <h2 className="modal-title-responsive">Add Task</h2>
          <div className="modal-subtitle">
            Add the required details for the new Tasks
          </div>
        </div>
        <div className="modal-body-responsive modal-scrollable-content">
          <form
            id="add-task-form"
            className="add-task-form modal-form-responsive"
            onSubmit={handleSubmit}
          >
            <label>
              <span className="label-row">
                Add Task<span className="required">*</span>
              </span>
              <input
                type="text"
                placeholder="Add Task"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>

            <div className="modal-row modal-form-row-responsive">

            </div>

            <div className="modal-row">
              <label>
                <span className="label-row">
                  Priority<span className="required">*</span>
                </span>
                <Select
                  options={priorityOptions}
                  value={
                    priorityOptions.find(
                      (option) => option.value === priority
                    ) || null
                  }
                  onChange={(option) => setPriority(option ? option.value : "")}
                  placeholder="Select Priority"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isSearchable={false}
                  styles={{
                    container: (base) => ({
                      ...base,
                      marginTop: "2px",
                    }),
                  }}
                  required
                />
              </label>

              <label>
                <span className="label-row">Start Date</span>
                <input
                  type="date"
                  placeholder="Select a start date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <label>
                <span className="label-row">
                  Target End Date<span className="required">*</span>
                </span>
                <input
                  type="date"
                  placeholder="Select a date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                  required
                />
              </label>
            </div>

            <label>
              <span className="label-row">Project</span>
              <Select
                options={projectOptions}
                value={projectOptions.find(
                  (option) => option.value === project
                )}
                onChange={handleProjectChange}
                placeholder="Select Project"
                isLoading={loadingProjects}
                isSearchable={true}
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  container: (base) => ({
                    ...base,
                    marginTop: "2px",
                  }),
                }}
              />
            </label>

            <div className="modal-row radio-row">
              <label>
                <input
                  type="radio"
                  name="assignType"
                  checked={assignType === "group"}
                  onChange={() => handleAssignTypeChange("group")}
                />{" "}
                Group
              </label>
              <label>
                <input
                  type="radio"
                  name="assignType"
                  checked={assignType === "members"}
                  onChange={() => handleAssignTypeChange("members")}
                />{" "}
                Members
              </label>
            </div>
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <span className="label-row" id="task-employee-label">
                  {assignType === "group"
                    ? "Choose Group"
                    : selectionMode === "multi"
                      ? "Choose Employees (Multiple)"
                      : "Choose Employee"}
                  <span className="required">*</span>
                </span>
                {assignType === "members" && (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!currentUserId) return;

                        const userIdStr = String(currentUserId);

                        if (selectionMode === "single") {
                          if (String(selectedEmployee) === userIdStr) {
                            setSelectedEmployee("");
                          } else {
                            setSelectedEmployee(userIdStr);
                          }
                        } else {
                          // Multi mode
                          const isAssigned = selectedEmployees.some(
                            (id) => String(id) === userIdStr
                          );
                          if (isAssigned) {
                            setSelectedEmployees((prev) =>
                              prev.filter((id) => String(id) !== userIdStr)
                            );
                          } else {
                            setSelectedEmployees((prev) => [...prev, userIdStr]);
                          }
                        }
                      }}
                      style={{
                        background: "none",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        fontSize: "12px",
                        color: currentUserId ? "#3b82f6" : "#9ca3af",
                        cursor: currentUserId ? "pointer" : "not-allowed",
                        transition: "all 0.2s",
                      }}
                      disabled={!currentUserId}
                      onMouseEnter={(e) => {
                        if (currentUserId) {
                          e.currentTarget.style.backgroundColor = "#eff6ff";
                          e.currentTarget.style.borderColor = "#3b82f6";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentUserId) {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.borderColor = "#d1d5db";
                        }
                      }}
                    >
                      {(selectionMode === "single" &&
                        String(selectedEmployee) === String(currentUserId)) ||
                        (selectionMode === "multi" &&
                          selectedEmployees.some(
                            (id) => String(id) === String(currentUserId)
                          ))
                        ? "Unassign Me"
                        : "Assign to Me"}
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleSelectionMode();
                      }}
                      style={{
                        background: "none",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        fontSize: "12px",
                        color: "#3b82f6",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#eff6ff";
                        e.currentTarget.style.borderColor = "#3b82f6";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.borderColor = "#d1d5db";
                      }}
                    >
                      {selectionMode === "single" ? "Select Multiple" : "Select Single"}
                    </button>
                  </div>
                )}
              </div>
              {assignType === "group" ? (
                <>
                  <Select
                    className="react-select-container"
                    classNamePrefix="react-select"
                    options={allGroupOptions}
                    value={
                      allGroupOptions.find(
                        (opt) => opt.value === selectedGroup
                      ) || null
                    }
                    onChange={(option) =>
                      setSelectedGroup(option ? option.value : "")
                    }
                    isLoading={loadingGroups}
                    placeholder={
                      loadingGroups
                        ? "Loading groups..."
                        : hasUserGroups
                          ? "Select or search group..."
                          : "No groups available"
                    }
                    isDisabled={!hasUserGroups}
                    isClearable
                    styles={{
                      container: (base) => ({
                        ...base,
                        marginTop: "2px",
                      }),
                    }}
                  />
                  {!loadingGroups && !hasUserGroups && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "14px",
                        color: "#6b7280",
                        fontStyle: "italic",
                      }}
                    >
                      You haven't created any groups yet, but you can assign
                      tasks to existing groups. To create your own groups, go to
                      the Groups page.
                    </div>
                  )}
                  {!loadingGroups && isShowingAllGroups && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "14px",
                        color: "#4ade80",
                        fontStyle: "italic",
                      }}
                    >
                      Showing all available groups since you haven't created any
                      yet.
                    </div>
                  )}
                </>
              ) : selectionMode === "multi" ? (
                // Multi-select checkbox mode
                <>
                  {/*<div
                    style={{
                      marginTop: "8px",
                      marginBottom: "12px",
                      padding: "8px",
                      backgroundColor: "#f3f4f6",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#6b7280",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>
                      {selectedEmployees.length > 0 &&
                        `${selectedEmployees.length} employee${selectedEmployees.length !== 1 ? "s" : ""
                        } selected`}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (allVisibleSelected) {
                          setSelectedEmployees((prev) =>
                            prev.filter((id) => !visibleEmployeeIds.includes(id))
                          );
                        } else {
                          setSelectedEmployees((prev) => {
                            const next = new Set(prev);
                            visibleEmployeeIds.forEach((id) => next.add(id));
                            return Array.from(next);
                          });
                        }
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#3b82f6",
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontSize: "12px",
                        padding: "0",
                      }}
                    >
                      {allVisibleSelected ? "Clear Visible" : "Select Visible"}
                    </button>
                  </div>*/}
                  <div
                    style={{
                      marginBottom: "12px",
                    }}
                  >
                    <input
                      type="text"
                      value={employeeSearchTerm}
                      onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                      placeholder="Search by name or email"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "1px solid #d1d5db",
                        fontSize: "14px",
                        outline: "none",
                        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#2563eb";
                        e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.15)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                  <div
                    style={{
                      maxHeight: "280px",
                      overflowY: "auto",
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
                      boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
                    }}
                  >
                    {loadingEmployees ? (
                      <div style={{ padding: "16px", textAlign: "center", color: "#6b7280" }}>
                        Loading employees...
                      </div>
                    ) : employees.length === 0 ? (
                      <div style={{ padding: "16px", textAlign: "center", color: "#6b7280" }}>
                        No employees available
                      </div>
                    ) : filteredEmployees.length === 0 ? (
                      <div style={{ padding: "16px", textAlign: "center", color: "#6b7280" }}>
                        No matches for "{employeeSearchTerm.trim()}"
                      </div>
                    ) : (
                      filteredEmployees.map((emp, index) => (
                        <label
                          key={emp.id}
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "16px",
                            padding: "14px 18px",
                            borderBottom:
                              index !== filteredEmployees.length - 1
                                ? "1px solid #eef2f7"
                                : "none",
                            cursor: "pointer",
                            transition: "background-color 0.2s ease, transform 0.2s ease",
                            backgroundColor: selectedEmployees.includes(emp.id) ? "#edf4ff" : "transparent",
                          }}
                          onMouseEnter={(e) => {
                            if (!selectedEmployees.includes(emp.id)) {
                              e.currentTarget.style.backgroundColor = "#f4f6fb";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = selectedEmployees.includes(emp.id) ? "#edf4ff" : "transparent";
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              flex: 1,
                              flexWrap: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#1f2937",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                              }}
                            >
                              {emp.FirstName} {emp.LastName}
                            </span>
                            <span
                              style={{
                                fontSize: "13px",
                                color: "#6b7280",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                flex: 1,
                              }}
                            >
                              {emp.email}
                            </span>
                          </div>
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(emp.id)}
                            onChange={() =>
                              handleEmployeeCheckboxChange(emp.id)
                            }
                            style={{
                              cursor: "pointer",
                              width: "16px",
                              height: "16px",
                              accentColor: "#2563eb",
                              flexShrink: 0,
                            }}
                          />
                        </label>
                      ))
                    )}
                  </div>
                </>
              ) : (
                // Single-select dropdown mode
                <Select
                  inputId="task-employee-select"
                  aria-labelledby="task-employee-label"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  options={employeeOptions}
                  value={
                    employeeOptions.find(
                      (opt) => opt.value === selectedEmployee
                    ) || null
                  }
                  onChange={(option) =>
                    setSelectedEmployee(option ? option.value : "")
                  }
                  isLoading={loadingEmployees}
                  placeholder={
                    loadingEmployees
                      ? "Loading employees..."
                      : "Select or search employee..."
                  }
                  isClearable
                  styles={{
                    container: (base) => ({
                      ...base,
                      marginTop: "2px",
                    }),
                  }}
                  required
                />
              )}
            </div>
            <label>
              <span className="label-row">Description</span>
              <textarea
                placeholder="Add Description"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            <label>
              <span className="label-row">Attachments</span>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <input
                  type="file"
                  onChange={handleAttachmentInput}
                  disabled={loading}
                  style={{ cursor: loading ? "not-allowed" : "pointer" }}
                  multiple
                />
                {attachments.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                  >
                    {attachments.map((fileItem, index) => (
                      <div
                        key={`${fileItem.name}-${fileItem.lastModified}-${index}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: "#f3f4ff",
                          borderRadius: 8,
                          padding: "8px 12px",
                          fontSize: "0.9rem",
                          gap: 12,
                        }}
                      >
                        <span
                          style={{
                            color: "#333",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                          title={fileItem.name}
                        >
                          {fileItem.name}
                          {fileItem.size
                            ? ` (${formatFileSize(fileItem.size)})`
                            : ""}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(index)}
                          disabled={loading}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#d32f2f",
                            fontWeight: 600,
                            cursor: loading ? "not-allowed" : "pointer",
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <span
                  style={{
                    fontSize: "0.78rem",
                    color: "#666",
                  }}
                >
                  {/* Up to {MAX_TASK_ATTACHMENTS} files, 15 MB each.*/}
                  The maximum file size is 15 MB.
                </span>
                {attachmentError && (
                  <span
                    style={{
                      color: "#d32f2f",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                    }}
                  >
                    {attachmentError}
                  </span>
                )}
              </div>
            </label>
            {error && <div className="modal-error-responsive">{error}</div>}
          </form>
        </div>
        <div className="modal-footer-responsive">
          <button
            type="submit"
            form="add-task-form"
            className="modal-btn-primary-responsive"
            disabled={loading}
          >
            {loading ? "Creating..." : "Add Task"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTaskModal;
